import React, { useMemo } from 'react';
import { StockHistoryEntry, StockLearnStatus } from '../../hooks/useStockHistory';
import { CollectibleStock, getCollectibleLogoUrl } from '../../config/collectiblesConfig';
import { cn } from '../../utils/cn';

interface BingoCardProps {
  stocks: StockHistoryEntry[];
  /** Tickers from the category pool to fill empty cells */
  categoryTickers?: string[];
  maxCells?: number;
  /** When provided, this stock is pinned to the top-left cell with a red highlight */
  currentSymbol?: string;
  /** When true, the currentSymbol cell plays a stamp-slam animation */
  animateCurrentSymbol?: boolean;
  /** When true, treat currentSymbol as at least 'guessed' even if not yet in stocks array (race condition fix) */
  currentSymbolDidWin?: boolean;
  /** Map of ticker symbol -> collectible data for displaying logos */
  collectibles?: Map<string, CollectibleStock>;
}

export const STATUS_BG: Record<StockLearnStatus | 'empty', string> = {
  learnt: 'bg-emerald-500',
  guessed: 'bg-amber-400',
  empty: 'bg-gray-300 dark:bg-gray-600',
};

export const STATUS_TEXT: Record<StockLearnStatus | 'empty', string> = {
  learnt: 'text-white',
  guessed: 'text-black',
  empty: 'text-gray-500 dark:text-gray-400',
};

/** Deterministic shuffle using a simple seed so the empty tickers stay stable per render */
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const copy = [...arr];
  let s = seed;
  for (let i = copy.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export const BingoCard: React.FC<BingoCardProps> = ({ stocks, categoryTickers = [], maxCells = 15, currentSymbol, animateCurrentSymbol, currentSymbolDidWin, collectibles }) => {
  const playedTickers = useMemo(() => new Set(stocks.map((s) => s.stockSymbol)), [stocks]);

  const emptyTickers = useMemo(() => {
    const available = categoryTickers.filter((t) => !playedTickers.has(t));
    return seededShuffle(available, 42);
  }, [categoryTickers, playedTickers]);

  const cells = useMemo(() => {
    type Cell = { ticker: string; status: StockLearnStatus | 'empty'; title: string; highlighted: boolean; collectible?: CollectibleStock; logoUrl?: string | null };
    const result: Cell[] = [];

    // Separate the current symbol from the rest
    let remaining = [...stocks];
    let pinnedStatus: StockLearnStatus | 'empty' | null = null;

    if (currentSymbol) {
      const idx = remaining.findIndex((s) => s.stockSymbol === currentSymbol);
      let pinnedLogoUrl: string | null = null;
      if (idx !== -1) {
        pinnedStatus = remaining[idx].status;
        pinnedLogoUrl = remaining[idx].logoUrl;
        remaining.splice(idx, 1);
      } else {
        // If user just won this stock but API hasn't returned it yet, show as 'guessed'
        pinnedStatus = currentSymbolDidWin ? 'guessed' : 'empty';
      }

      result.push({
        ticker: currentSymbol,
        status: pinnedStatus,
        title: pinnedStatus === 'empty'
          ? `${currentSymbol} — Yet to guess`
          : `${currentSymbol}`,
        highlighted: true,
        collectible: collectibles?.get(currentSymbol),
        logoUrl: pinnedLogoUrl,
      });
    }

    // Fill played stocks (up to maxCells minus pinned)
    const slotsForPlayed = maxCells - result.length;

    // Sort remaining so collectibles appear first
    const sortedRemaining = [...remaining];
    if (collectibles && collectibles.size > 0) {
      sortedRemaining.sort((a, b) => {
        const aCollect = collectibles.has(a.stockSymbol) ? 0 : 1;
        const bCollect = collectibles.has(b.stockSymbol) ? 0 : 1;
        return aCollect - bCollect;
      });
    }

    const displayStocks = sortedRemaining.slice(0, slotsForPlayed);
    for (const entry of displayStocks) {
      result.push({
        ticker: entry.stockSymbol,
        status: entry.status,
        title: `${entry.companyName} (${entry.stockSymbol})`,
        highlighted: false,
        collectible: collectibles?.get(entry.stockSymbol),
        logoUrl: entry.logoUrl,
      });
    }

    // Fill empty slots
    const emptyNeeded = Math.max(0, maxCells - result.length);
    let emptyIdx = 0;
    for (let i = 0; i < emptyNeeded; i++) {
      const ticker = emptyIdx < emptyTickers.length ? emptyTickers[emptyIdx++] : '';
      result.push({
        ticker,
        status: 'empty',
        title: ticker ? `${ticker} — Yet to guess` : 'Yet to guess',
        highlighted: false,
      });
    }

    return result;
  }, [stocks, currentSymbol, maxCells, emptyTickers, currentSymbolDidWin, collectibles]);

  return (
    <>
      <style>{`
        @keyframes stamp-slam {
          0% { transform: scale(2.5) rotate(-12deg); opacity: 0; }
          60% { transform: scale(0.95) rotate(1deg); opacity: 1; }
          80% { transform: scale(1.05) rotate(-0.5deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
      `}</style>
      <div className="grid grid-cols-5 gap-1.5">
        {cells.map((cell, i) => (
          <div
            key={`${cell.ticker || 'empty'}-${i}`}
            className={cn(
              'relative flex items-center justify-center rounded-md aspect-square min-h-[3rem]',
              'font-mono text-sm font-semibold select-none',
              'transition-colors overflow-hidden',
              STATUS_BG[cell.status],
              cell.highlighted ? 'text-red-500 ring-2 ring-red-500' : STATUS_TEXT[cell.status],
              cell.collectible && !cell.highlighted && 'ring-2 ring-yellow-400'
            )}
            style={
              cell.highlighted && animateCurrentSymbol
                ? { animation: 'stamp-slam 0.5s ease-out 0.3s both' }
                : undefined
            }
            title={cell.title}
          >
            {cell.collectible ? (
              <img
                src={getCollectibleLogoUrl(cell.collectible)}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-contain p-1 opacity-50 pointer-events-none"
              />
            ) : cell.logoUrl ? (
              <img
                src={cell.logoUrl}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-contain p-1.5 opacity-40 pointer-events-none"
              />
            ) : null}
            <span className="relative z-10">{cell.ticker}</span>
          </div>
        ))}
      </div>
    </>
  );
};
